<template>
  <div class="notifications-list">
    <el-scrollbar :style="{ maxHeight: `${containerHeight}px` }">
      <notification
        v-if="doNotDisturb"
        center-icon
        class="ma-24"
        :color="'brand-secondary-accent'"
        :currentTab="currentTab"
      >
        <template #icon>
          <notification-disabled-icon
            :bg-color="'accent'"
            :color="'brand-primary'"
            size="48"
          />
        </template>
        <div class="title el-text--ellipsis">
          {{ $t('Do not disturb is on') }}
        </div>
        {{ $t('Mutes all pop-up notifications. Notifications will still be available in the notification center.') }}
        <template #actions>
          <el-button
            type="ghost"
            @click="enableNotifications"
          >
            {{ $t('Turn off') }}
          </el-button>
        </template>
      </notification>

      <template v-if="notifications.length">
        <div
          v-if="inProgressNotifications.length"
          class="ma-24"
        >
          <notification
            v-for="notification in inProgressNotifications"
            :key="notification.id"
            :notification="notification"
            :currentTab="currentTab"
          />
        </div>
        <div
          v-if="filterPanel && mainNotifications.length"
          class="notifications-panel mt-24 mb-16"
        >
          <div class="filters">
            <el-dropdown @command="onFilterApply">
              <el-button
                icon="chevron-down"
                right-icon
                type="ghost"
              >
                {{ NOTIFICATION_TYPE_LABELS[filterType] }}
              </el-button>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="type in NOTIFICATION_CENTER_FILTER_TYPES"
                  :key="type"
                  :command="{ type }"
                >
                  {{ NOTIFICATION_TYPE_LABELS[type] }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div class="actions">
            <el-button
              icon="clean-up-o"
              type="ghost"
              @click="clear"
            >
              {{ filterTypeAll ? $t('Clear all') : $t('Clear') }}
            </el-button>
          </div>
        </div>
        <div
          v-if="notificationsFiltered.length"
          class="mx-24 mb-24 my-16"
        >
          <notification
            v-for="notification in notificationsFiltered"
            :key="notification.id"
            :notification="notification"
            :currentTab="currentTab"
          />
        </div>
        <template v-else-if="!filterTypeAll">
          <empty
            class="pt-16 pb-0"
            :description="$t('Try to change the search parameters')"
            icon="search-o"
          >
            <template #title>
              <div class="empty-title">
                {{ $t('No notifications found') }}
              </div>
            </template>
          </empty>
        </template>
      </template>

      <empty
        v-else
        class="empty"
        icon="bell-o"
      >
        <template #title>
          <div class="empty-title">
            {{ $t('No recent notifications') }}
          </div>
        </template>
      </empty>
    </el-scrollbar>
  </div>
</template>

<script>
  import Empty from '@/common/shared/Empty.vue';
  import Notification from '@/common/components/notification-center/Notification.vue';
  import NotificationDisabledIcon from '@/common/components/notification-center/NotificationDisabledIcon.vue';

  import {
    NotificationType,
    NOTIFICATION_CENTER_FILTER_TYPES,
    NOTIFICATION_TYPE_LABELS,
  } from '@/common/constants/notifications.constants';
  import { mapGetters } from 'vuex';

  export default {
    name: 'NotificationsList',
    components: {
      Empty,
      Notification,
      NotificationDisabledIcon,
    },
    props: {
      notifications: {
        type: Array,
        default: () => []
      },
      filterPanel: {
        type: Boolean,
        default: true
      },
      currentTab: {
        type: String,
        default: 'notifications'
      }
    },
    data() {
      return {
        NotificationType,
        NOTIFICATION_CENTER_FILTER_TYPES,
        NOTIFICATION_TYPE_LABELS,
        filterType: NotificationType.ALL
      };
    },
    computed: {
      ...mapGetters({
        doNotDisturb: 'notifications/doNotDisturb',
        tasksNotifications: 'notifications/tasksNotifications',
      }),
      containerHeight() {
        return (document.body.offsetHeight * 0.9) - 64;
      },
      inProgressNotifications() {
        return this.notifications.filter(it => it.type === NotificationType.PROGRESS);
      },
      mainNotifications() {
        return this.notifications.filter(it => it.type !== NotificationType.PROGRESS);
      },
      notificationsFiltered() {
        return this.filterTypeAll
          ? this.mainNotifications
          : this.mainNotifications.filter(it => it.type === this.filterType);
      },
      filterTypeAll() {
        return this.filterType === NotificationType.ALL;
      }
    },
    methods: {
      onFilterApply({ type }) {
        this.filterType = type;
      },
      clear() {
        const ids = this.notificationsFiltered.map(it => it.id);
        const tasksIds = this.tasksNotifications.map(it => it.id);
        this.$store.dispatch('notifications/REMOVE', ids.concat(tasksIds));
      },
      enableNotifications() {
        this.$store.dispatch('notifications/UNMUTE');
      }
    }
  };
</script>

<style scoped lang="scss">
.notifications-panel {
  display: flex;
  width: 100%;
  padding: 0 16px;

  .filters {
    flex: 1 1 auto;
  }

  .actions {
    flex: 0 0 auto;
  }
}

.empty-title {
  margin-top: 15px;
  font-size: $av-font-size-l;
  line-height: $av-line-height-m;
}

.empty {
  padding: 40px 0;
}
</style>
